import { dryrun } from "@permaweb/aoconnect"

import { useQuery } from "@tanstack/react-query"

import { DELEGATE_PROCESS } from "../settings"

export interface DelegationCapacity {
  totalCapacity: number
  delegated: number
  available: number
  delegationPrefs?: Array<{
    walletTo: string
    factor: number
  }>
}

/**
 * Custom hook to fetch delegation capacity for a wallet
 * @param walletAddress AO wallet address
 * @returns Delegation capacity information
 */
export function useDelegationCapacity(walletAddress: string | undefined): {
  data: DelegationCapacity | undefined
  isLoading: boolean
  error: Error | null
  refetch: () => void
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [`delegations`, walletAddress],
    enabled: !!walletAddress,
    queryFn: async (): Promise<DelegationCapacity | undefined> => {
      if (!walletAddress) return undefined

      try {
        const result = await dryrun({
          process: DELEGATE_PROCESS,
          tags: [
            {
              name: "Action",
              value: "Get-Delegations",
            },
            {
              name: "Wallet",
              value: walletAddress,
            },
          ],
        })

        // Check the first message from result
        const firstMessage = result.Messages[0]

        if (!firstMessage) {
          console.error("No messages found")
          return undefined
        }

        const decodedData = JSON.parse(firstMessage.Data)

        console.log("DDD:", decodedData)

        // Total Capacity is 10000 units (100%)
        const totalCapacity = 10_000

        // Calculate already delegated capacity (sum of all delegations)
        let delegated = 0
        if (decodedData.delegationPrefs) {
          delegated = decodedData.delegationPrefs.reduce(
            (sum: number, pref: { factor: number }) => sum + pref.factor,
            0,
          )
        }

        // Calculate available capacity
        const available = totalCapacity - delegated

        return {
          totalCapacity,
          delegated,
          available,
          delegationPrefs: decodedData.delegationPrefs,
        }
      } catch (err) {
        console.error("Error fetching delegations:", err)
        throw err
      }
    },
  })

  return { data, isLoading, error: error as Error | null, refetch }
}

export default useDelegationCapacity
